import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import BlockMenu from './BlockMenu';

const DEFAULT_ANIMATION_DURATION = 220;
const FixedMenuHeaderActionsContext = createContext(null);

export const useFixedMenuHeaderActions = (actions) => {
    const setRegisteredActions = useContext(FixedMenuHeaderActionsContext);

    useEffect(() => {
        if (!setRegisteredActions) return undefined;
        setRegisteredActions(actions);
        return () => setRegisteredActions(null);
    }, [actions, setRegisteredActions]);
};

const FixedMenu = ({
    title,
    iconClass,
    headerActions,
    expanded,
    setExpanded,
    className = '',
    animate = false,
    animationDuration = DEFAULT_ANIMATION_DURATION,
    children,
}) => {
    const [rendered, setRendered] = useState(expanded);
    const [active, setActive] = useState(false);
    const [registeredActions, setRegisteredActions] = useState(null);
    const registerHeaderActions = useCallback((actions) => setRegisteredActions(actions), []);

    useEffect(() => {
        if (!animate) {
            return undefined;
        }

        if (expanded) {
            setRendered(true);
            const show = () => setActive(true);
            const frame = typeof window !== 'undefined' && window.requestAnimationFrame
                ? window.requestAnimationFrame(show)
                : setTimeout(show, 0);
            return () => {
                if (typeof frame === 'number' && typeof window !== 'undefined' && window.cancelAnimationFrame) {
                    window.cancelAnimationFrame(frame);
                } else {
                    clearTimeout(frame);
                }
            };
        }

        setActive(false);
        const timer = setTimeout(() => setRendered(false), animationDuration);
        return () => clearTimeout(timer);
    }, [animate, animationDuration, expanded]);

    // Non-animated menus do not need mirrored state. Keeping `expanded` in both
    // props and state made opening ProjectImageSelector synchronously schedule
    // more updates from this effect. Derive both values directly in that mode.
    const visible = animate ? rendered : expanded;
    const isActive = animate ? active : expanded;
    if (!visible) return null;

    const menu = (
        <div
            className={[
                'outer-fixed',
                className,
                animate ? 'fixed-menu-animated' : '',
                isActive ? 'is-open' : 'is-closing',
            ].filter(Boolean).join(' ')}
            aria-hidden={animate && !expanded}
        >
            <div className="container fixed-menu-container">
                <FixedMenuHeaderActionsContext.Provider value={registerHeaderActions}>
                    <BlockMenu title={title} iconClass={iconClass} headerActions={registeredActions || headerActions} expanded={visible} setExpanded={setExpanded}>
                        {children}
                    </BlockMenu>
                </FixedMenuHeaderActionsContext.Provider>
            </div>
        </div>
    );

    return typeof document === 'undefined'
        ? menu
        : createPortal(menu, document.body);
};

FixedMenu.propTypes = {
    title: PropTypes.string.isRequired,
    iconClass: PropTypes.string,
    headerActions: PropTypes.node,
    expanded: PropTypes.bool.isRequired,
    setExpanded: PropTypes.func.isRequired,
    className: PropTypes.string,
    animate: PropTypes.bool,
    animationDuration: PropTypes.number,
    children: PropTypes.node.isRequired,
};

export default FixedMenu;
